/**
 * SIM session (Layer 3) — signs the console in when sim mode is on.
 *
 * Sim mode already swaps the data source (`mockApi.ts`, Layer 1) and the
 * wallet (`simWallet.ts`, Layer 2), but the console still refused to render:
 * `AuthGuard` reads `useOrgAuthStore` tokens and redirects to `/login` before
 * any of it is reached. So `?sim=1` alone got you a login form, and every sim
 * URL had to be preceded by hand-seeding `sessionStorage`.
 *
 * There is nothing to log in to in sim mode — the OAuth `apiBase` points at a
 * locally-run yaas-api that is not running, which is the whole reason the mock
 * layer exists. So the session is part of the mock, exactly like the data and
 * the wallet: this seeds the store with `simMeResponse()`, the same identity
 * `mockApi.ts` serves its data as.
 *
 * Two ordering constraints:
 *
 *   1. It must run BEFORE React renders. `AuthGuard` decides on its first
 *      render, and its redirect drops the query string — so a seed that landed
 *      one tick late would take the `?sim=1` with it and never recover.
 *   2. The `me` it seeds must be complete. `useOrgAuth` re-fetches a profile
 *      that fails `needsOrgProfileRefresh` and, when that fetch rejects,
 *      clears the session and navigates to `/login`. `simMeResponse` populates
 *      every field that check inspects.
 *
 * Inert everywhere sim mode is off, and `isSimMode()` hard-returns false in
 * production bundles, so this cannot mint a session in a shipped app.
 */

import { useOrgAuthStore } from "@solomon-apps/shared";

import { simMeResponse } from "./mockApi";
import { isSimMode } from "./simMode";

/** A day out, so a sim session never expires mid-session. */
const SIM_TOKEN_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Seed the org auth store with the sim identity when sim mode is on.
 *
 * A live session is left alone: only an absent or expired token is replaced,
 * which is the same condition `AuthGuard` redirects on. Adding `?sim=1` to a
 * tab that is genuinely signed in therefore swaps the data layer without
 * disturbing who you are signed in as.
 */
export function initSimSession(): void {
    if (typeof window === "undefined") return;
    if (!isSimMode()) return;

    const { tokens, setTokens, setMe } = useOrgAuthStore.getState();
    // Mirrors AuthGuard's own staleness test, so a session it would reject is
    // exactly the session this replaces.
    if (tokens && tokens.expiresAt >= Date.now() + 30_000) return;

    setTokens({
        accessToken: "sim-access-token",
        refreshToken: "sim-refresh-token",
        expiresAt: Date.now() + SIM_TOKEN_TTL_MS,
        scope: "openid profile"
    });
    // `setMe` also derives the operating contexts and the selected context, so
    // the context switcher is populated the same way a real sign-in leaves it.
    setMe(simMeResponse());
}
